import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Button, 
  Card, 
  Descriptions, 
  Image, 
  Tag, 
  Spin, 
  Typography,
  Space,
  Row,
  Col,
  Breadcrumb,
  Modal,
  Form,
  Input,
  InputNumber,
  Divider,
  Empty,
  Tooltip,
  Avatar,
  Badge,
  Tabs,
  Select
} from 'antd';
import { 
  ArrowLeftOutlined, 
  PlusOutlined, 
  EyeOutlined, 
  EditOutlined,
  CheckCircleOutlined,
  PictureOutlined,
  CloseOutlined,
  UserOutlined,
  HeartOutlined,
  TeamOutlined,
  CameraOutlined,
  RobotOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { lifetreeApi } from '../api/lifetreeApi';
import { 
  CharacterDetailResponse, 
  CharacterStateWithRelationships, 
  UpdateCharacterStateRequest,
  KeyValuePair,
  CreateImageResponse,
  StateImage,
  RelationshipItem,
  ApproveImageRequest,
  CreateRelationshipRequest,
  CharacterItem,
  CreateStateImagePromptResponse
} from '../api/models/Lifetree';
import { useToast } from '../components/Toast';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

interface UpdateStateModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: CharacterStateWithRelationships | null;
  onSubmit: (data: UpdateCharacterStateRequest) => Promise<void>;
  isLoading: boolean;
}

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: CreateImageResponse | null;
  onApprove: (imageId: string) => Promise<void>;
  isLoading: boolean;
}

interface ImageGalleryProps {
  images: StateImage[];
  onCreateImage: () => void;
  createImageLoading: boolean;
  onApproveImage: (imageId: string) => void;
  onSetAsCharacterImage: (imageUrl: string) => void;
  onSetAsNodeImage: (imageUrl: string) => void;
  onSetAsLifetreeImage: (imageUrl: string) => void;
  approveImageLoading: string | null;
  setCharacterImageLoading: boolean;
  setNodeImageLoading: boolean;
  setLifetreeImageLoading: boolean;
  nodesUsingThisState: any[];
}

interface CreateRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableCharacters: CharacterItem[];
  onSubmit: (data: CreateRelationshipRequest) => Promise<void>;
  isLoading: boolean;
}

interface CreateImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (emotion?: string, description?: string) => Promise<void>;
  onGeneratePrompt: (emotion?: string, description?: string) => Promise<CreateStateImagePromptResponse>;
  isLoading: boolean;
  generatePromptLoading: boolean;
  referenceAvatar: string | null;
}

const EMOTIONS = [
  'happy',
  'sad',
  'angry',
  'surprised',
  'disappointed',
  'excited',
  'fearful',
  'disgusted',
  'confused'
];

const UpdateStateModal: React.FC<UpdateStateModalProps> = ({ isOpen, onClose, state, onSubmit, isLoading }) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<UpdateCharacterStateRequest>({
    name: '',
    age: 0,
    alias: '',
    occupation: '',
    imageUrl: '',
    description: '',
    displayedDescription: '',
    appearance: [{ key: '', value: '' }],
    innerSelf: [{ key: '', value: '' }],
    relationshipStyles: [{ key: '', value: '' }],
  });

  useEffect(() => {
    if (state && isOpen) {
      const stateData = {
        name: state.name,
        age: state.age,
        alias: state.alias || '',
        occupation: state.occupation || '',
        imageUrl: state.imageUrl || '',
        description: state.description || '',
        displayedDescription: state.displayedDescription || '',
        appearance: state.appearance.length > 0 ? state.appearance : [{ key: '', value: '' }],
        innerSelf: state.innerSelf.length > 0 ? state.innerSelf : [{ key: '', value: '' }],
        relationshipStyles: state.relationshipStyles.length > 0 ? state.relationshipStyles : [{ key: '', value: '' }],
      };
      setFormData(stateData);
      form.setFieldsValue(stateData);
    }
  }, [state, isOpen, form]);

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      
      const cleanData = {
        ...formData,
        appearance: (formData.appearance || []).filter(item => item.key.trim() && item.value.trim()),
        innerSelf: (formData.innerSelf || []).filter(item => item.key.trim() && item.value.trim()),
        relationshipStyles: (formData.relationshipStyles || []).filter(item => item.key.trim() && item.value.trim()),
      };

      await onSubmit(cleanData);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const addKeyValuePair = (field: 'appearance' | 'innerSelf' | 'relationshipStyles') => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), { key: '', value: '' }]
    }));
  };

  const updateKeyValuePair = (field: 'appearance' | 'innerSelf' | 'relationshipStyles', index: number, type: 'key' | 'value', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).map((item, i) => 
        i === index ? { ...item, [type]: value } : item
      )
    }));
  };

  const removeKeyValuePair = (field: 'appearance' | 'innerSelf' | 'relationshipStyles', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          Cập nhật State: {state?.name}
        </Space>
      }
      open={isOpen && !!state}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Hủy
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit}
          loading={isLoading}
        >
          Cập nhật State
        </Button>,
      ]}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                label="Tên" 
                name="name"
                rules={[{ required: true, message: 'Vui lòng nhập tên state!' }]}
              >
                <Input 
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nhập tên state"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item 
                label="Tuổi" 
                name="age"
                rules={[{ required: true, message: 'Vui lòng nhập tuổi!' }]}
              >
                <InputNumber 
                  min={0}
                  max={120}
                  value={formData.age}
                  onChange={(value) => setFormData(prev => ({ ...prev, age: value || 0 }))}
                  placeholder="Nhập tuổi"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Biệt danh" name="alias">
                <Input 
                  value={formData.alias || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, alias: e.target.value }))}
                  placeholder="Nhập biệt danh"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Nghề nghiệp" name="occupation">
                <Input 
                  value={formData.occupation || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, occupation: e.target.value }))}
                  placeholder="Nhập nghề nghiệp"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="URL ảnh" name="imageUrl">
                <Input 
                  value={formData.imageUrl || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                  placeholder="Nhập URL ảnh"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Mô tả hiển thị" name="displayedDescription">
            <Input 
              value={formData.displayedDescription || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, displayedDescription: e.target.value }))}
              placeholder="Nhập mô tả ngắn gọn sẽ hiển thị trong game"
            />
          </Form.Item>

          <Form.Item label="Mô tả chi tiết" name="description">
            <TextArea
              rows={4}
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Nhập mô tả chi tiết về state này"
            />
          </Form.Item>

          {/* Appearance */}
          <Form.Item label="Ngoại hình" required>
            <Space direction="vertical" style={{ width: '100%' }}>
              {(formData.appearance || []).map((item, index) => (
                <Row key={index} gutter={8} wrap={false} style={{ width: '100%' }} align="top">
                  <Col>
                    <Input
                      placeholder="Đặc điểm"
                      value={item.key}
                      onChange={(e) => updateKeyValuePair('appearance', index, 'key', e.target.value)}
                      style={{ width: 200 }}
                    />
                  </Col>
                  <Col flex="auto">
                    <TextArea
                      placeholder="Mô tả"
                      value={item.value}
                      onChange={(e) => updateKeyValuePair('appearance', index, 'value', e.target.value)}
                      style={{ width: '100%' }}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                    />
                  </Col>
                  <Col>
                    <Button 
                      icon={<CloseOutlined />}
                      onClick={() => removeKeyValuePair('appearance', index)}
                      danger
                      size="small"
                    />
                  </Col>
                </Row>
              ))}
              <Button 
                type="dashed" 
                onClick={() => addKeyValuePair('appearance')}
                icon={<PlusOutlined />}
                style={{ width: '100%' }}
              >
                Thêm đặc điểm ngoại hình
              </Button>
            </Space>
          </Form.Item>

          {/* Inner Self */}
          <Form.Item label="Tâm lý bên trong" required>
            <Space direction="vertical" style={{ width: '100%' }}>
              {(formData.innerSelf || []).map((item, index) => (
                <Row key={index} gutter={8} wrap={false} style={{ width: '100%' }} align="top">
                  <Col>
                    <Input
                      placeholder="Đặc điểm tâm lý"
                      value={item.key}
                      onChange={(e) => updateKeyValuePair('innerSelf', index, 'key', e.target.value)}
                      style={{ width: 200 }}
                    />
                  </Col>
                  <Col flex="auto">
                    <TextArea
                      placeholder="Mô tả"
                      value={item.value}
                      onChange={(e) => updateKeyValuePair('innerSelf', index, 'value', e.target.value)}
                      style={{ width: '100%' }}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                    />
                  </Col>
                  <Col>
                    <Button 
                      icon={<CloseOutlined />}
                      onClick={() => removeKeyValuePair('innerSelf', index)}
                      danger
                      size="small"
                    />
                  </Col>
                </Row>
              ))}
              <Button 
                type="dashed" 
                onClick={() => addKeyValuePair('innerSelf')}
                icon={<PlusOutlined />}
                style={{ width: '100%' }}
              >
                Thêm đặc điểm tâm lý
              </Button>
            </Space>
          </Form.Item>

          {/* Relationship Styles */}
          <Form.Item label="Phong cách quan hệ" required>
            <Space direction="vertical" style={{ width: '100%' }}>
              {(formData.relationshipStyles || []).map((item, index) => (
                <Row key={index} gutter={8} wrap={false} style={{ width: '100%' }} align="top">
                  <Col>
                    <Input
                      placeholder="Loại quan hệ"
                      value={item.key}
                      onChange={(e) => updateKeyValuePair('relationshipStyles', index, 'key', e.target.value)}
                      style={{ width: 200 }}
                    />
                  </Col>
                  <Col flex="auto">
                    <TextArea
                      placeholder="Phong cách"
                      value={item.value}
                      onChange={(e) => updateKeyValuePair('relationshipStyles', index, 'value', e.target.value)}
                      style={{ width: '100%' }}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                    />
                  </Col>
                  <Col>
                    <Button 
                      icon={<CloseOutlined />}
                      onClick={() => removeKeyValuePair('relationshipStyles', index)}
                      danger
                      size="small"
                    />
                  </Col>
                </Row>
              ))}
              <Button 
                type="dashed" 
                onClick={() => addKeyValuePair('relationshipStyles')}
                icon={<PlusOutlined />}
                style={{ width: '100%' }}
              >
                Thêm phong cách quan hệ
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ 
  isOpen, 
  onClose, 
  image, 
  onApprove, 
  isLoading 
}) => {
  return (
    <Modal
      title={
        <Space>
          <PictureOutlined />
          Preview Ảnh
        </Space>
      }
      open={isOpen && !!image}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Hủy
        </Button>,
        <Button 
          key="approve" 
          type="primary" 
          onClick={() => image && onApprove(image.id)}
          loading={isLoading}
          icon={<CheckCircleOutlined />}
        >
          Duyệt ảnh
        </Button>,
      ]}
    >
      {image && (
        <div style={{ textAlign: 'center' }}>
          <Image
            src={image.url}
            alt="Generated character state"
            style={{
              maxWidth: '100%',
              maxHeight: '400px',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
            preview={{
              mask: (
                <div style={{ color: 'white' }}>
                  <EyeOutlined style={{ fontSize: 16, marginRight: 8 }} />
                  Xem chi tiết
                </div>
              )
            }}
          />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">
              Kích thước: {image.width} x {image.height} pixels
            </Text>
          </div>
          
          {image.variants && image.variants.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <Title level={5}>Các phiên bản khác:</Title>
              <Row gutter={[16, 16]} justify="center">
                {image.variants.map((variant, index) => (
                  <Col key={index}>
                    <Image
                      src={variant.url}
                      alt={`Variant ${index + 1}`}
                      width={100}
                      height={100}
                      style={{ 
                        objectFit: 'cover',
                        objectPosition: 'top center',
                        borderRadius: '6px',
                        border: '2px solid #f0f0f0'
                      }}
                      preview={{
                        mask: (
                          <div style={{ color: 'white', fontSize: '12px' }}>
                            <EyeOutlined />
                          </div>
                        )
                      }}
                    />
                    <div style={{ textAlign: 'center', marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {variant.width}x{variant.height}
                      </Text>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  images, 
  onCreateImage, 
  createImageLoading,
  onApproveImage,
  onSetAsCharacterImage,
  onSetAsNodeImage,
  onSetAsLifetreeImage,
  approveImageLoading,
  setCharacterImageLoading,
  setNodeImageLoading,
  setLifetreeImageLoading,
  nodesUsingThisState
}) => {
  const THUMB_WIDTH = 100;
  const THUMB_HEIGHT = 150;
  if (images.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Chưa có ảnh nào"
        >
          <Button 
            type="primary" 
            icon={<CameraOutlined />}
            onClick={onCreateImage}
            loading={createImageLoading}
          >
            Tạo ảnh đầu tiên
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={onCreateImage}
          loading={createImageLoading}
        >
          Tạo ảnh mới
        </Button>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {images.map((image) => (
          <Card key={image.id} size="small">
            <Row gutter={[16, 16]} align="middle">
              {/* Cột 1: Ảnh */}
              <Col xs={24} sm={4} md={3}>
                <div style={{ 
                  position: 'relative',
                  width: `${THUMB_WIDTH}px`,
                  height: `${THUMB_HEIGHT}px`, // 2:3 ratio
                  margin: '0 auto'
                }}>
                  <Image
                    src={image.url}
                    alt="Character State"
                    width={THUMB_WIDTH}
                    height={THUMB_HEIGHT}
                    style={{ 
                      objectFit: 'cover', 
                      objectPosition: 'top center',
                      borderRadius: '8px',
                      border: '2px solid #f0f0f0'
                    }}
                    preview={{
                      mask: (
                        <div style={{ 
                          color: 'white',
                          background: 'rgba(0,0,0,0.5)',
                          borderRadius: '4px',
                          padding: '4px 8px'
                        }}>
                          <EyeOutlined style={{ marginRight: 4 }} />
                          Xem chi tiết
                        </div>
                      )
                    }}
                  />
                  <div style={{ 
                    position: 'absolute', 
                    top: 6, 
                    right: 6,
                    background: image.status === 'active' ? '#52c41a' : '#fa8c16',
                    color: 'white',
                    padding: '3px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}>
                    {image.status.toUpperCase()}
                  </div>
                  {image.unlocked && (
                    <div style={{ 
                      position: 'absolute', 
                      bottom: 6, 
                      left: 6,
                      background: '#52c41a',
                      color: 'white',
                      padding: '3px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      UNLOCKED
                    </div>
                  )}
                </div>
              </Col>

              {/* Cột 2: Kích thước ảnh */}
              <Col xs={24} sm={5} md={3}>
                <Space direction="vertical" size="small">
                  <div>
                    <Text strong>Kích thước:</Text>
                    <br />
                    <Text>{image.width} x {image.height} px</Text>
                  </div>
                </Space>
              </Col>

              {/* Cột 3: Tags */}
              <Col xs={24} sm={7} md={4}>
                {image.tags && image.tags.length > 0 ? (
                  <Space wrap>
                    {image.tags.map((tagVal) => (
                      <Tag key={tagVal}>{tagVal}</Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">Không có tags</Text>
                )}
              </Col>

              {/* Cột 4: Variants */}
              <Col xs={24} sm={8} md={5}>
                {image.variants && image.variants.length > 0 ? (
                  <Space wrap>
                    {image.variants.map((variant) => (
                      <Image
                        key={variant.id}
                        src={variant.url}
                        alt={variant.type}
                        width={THUMB_WIDTH}
                        height={THUMB_HEIGHT}
                        style={{ objectFit: 'cover', objectPosition: 'top center', borderRadius: '4px', border: '1px solid #f0f0f0' }}
                        preview={{
                          mask: (
                            <div style={{ color: 'white', fontSize: '12px' }}>
                              <EyeOutlined />
                            </div>
                          )
                        }}
                      />
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">Không có variant</Text>
                )}
              </Col>

              {/* Cột 5: Action group */}
              <Col xs={24} sm={24} md={9}>
                <Space wrap>
                  {image.status === 'draft' && (
                    <Button 
                      type="primary"
                      size="small"
                      icon={<CheckCircleOutlined />}
                      onClick={() => onApproveImage(image.id)}
                      loading={approveImageLoading === image.id}
                    >
                      Duyệt ảnh
                    </Button>
                  )}
                  
                  <Button 
                    size="small"
                    icon={<UserOutlined />}
                    onClick={() => onSetAsCharacterImage(image.url)}
                    loading={setCharacterImageLoading}
                    disabled={image.status !== 'active'}
                  >
                    Làm ảnh nhân vật
                  </Button>
                  
                  {nodesUsingThisState.length > 0 && (
                    <Button 
                      size="small"
                      icon={<PictureOutlined />}
                      onClick={() => onSetAsNodeImage(image.url)}
                      loading={setNodeImageLoading}
                      disabled={image.status !== 'active'}
                    >
                      Làm ảnh node ({nodesUsingThisState.length})
                    </Button>
                  )}
                  
                  <Button 
                    size="small"
                    icon={<PictureOutlined />}
                    onClick={() => onSetAsLifetreeImage(image.url)}
                    loading={setLifetreeImageLoading}
                    disabled={image.status !== 'active'}
                  >
                    Làm ảnh lifetree
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        ))}
      </div>
    </div>
  );
};

const CreateRelationshipModal: React.FC<CreateRelationshipModalProps> = ({ 
  isOpen, 
  onClose, 
  availableCharacters,
  onSubmit, 
  isLoading 
}) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<CreateRelationshipRequest>({
    targetId: '',
    initScore: 0,
    initTitle: '',
    intent: '',
    detail: ''
  });

  const resetForm = () => {
    const initialData = {
      targetId: '',
      initScore: 0,
      initTitle: '',
      intent: '',
      detail: ''
    };
    setFormData(initialData);
    form.setFieldsValue(initialData);
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const cleanData = {
        ...formData,
        detail: formData.detail?.trim() || undefined
      };

      await onSubmit(cleanData);
      resetForm();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Tri kỷ/Người yêu';
    if (score >= 40) return 'Bạn thân';
    if (score >= 10) return 'Bạn bè';
    if (score >= -10) return 'Người quen';
    if (score >= -50) return 'Đối thủ';
    return 'Kẻ thù';
  };

  return (
    <Modal
      title={
        <Space>
          <TeamOutlined />
          Tạo mối quan hệ mới
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Hủy
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit}
          loading={isLoading}
        >
          Tạo mối quan hệ
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item 
          label="Nhân vật đích" 
          name="targetId"
          rules={[{ required: true, message: 'Vui lòng chọn nhân vật!' }]}
        >
          <Select
            placeholder="Chọn nhân vật"
            value={formData.targetId}
            onChange={(value) => setFormData(prev => ({ ...prev, targetId: value }))}
            showSearch
            optionFilterProp="children"
          >
            {availableCharacters.map(char => (
              <Select.Option key={char.id} value={char.id}>
                <Space>
                  <Avatar size="small" src={char.imageUrl} icon={<UserOutlined />} />
                  <span>{char.name}</span>
                  <Tag color={char.type === 'main' ? 'blue' : 'green'}>
                    {char.type.toUpperCase()}
                  </Tag>
                </Space>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              label="Điểm quan hệ ban đầu" 
              name="initScore"
              rules={[
                { required: true, message: 'Vui lòng nhập điểm quan hệ!' },
                { type: 'number', min: -100, max: 100, message: 'Điểm phải từ -100 đến 100' }
              ]}
            >
              <InputNumber 
                min={-100}
                max={100}
                value={formData.initScore}
                onChange={(value) => setFormData(prev => ({ ...prev, initScore: value || 0 }))}
                placeholder="Nhập điểm (-100 đến 100)"
                style={{ width: '100%' }}
              />
            </Form.Item>
            {formData.initScore !== null && (
              <div style={{ marginTop: -16, marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Mức độ: {getScoreLabel(formData.initScore)}
                </Text>
              </div>
            )}
          </Col>
          <Col span={12}>
            <Form.Item 
              label="Tiêu đề quan hệ" 
              name="initTitle"
              rules={[{ required: true, message: 'Vui lòng nhập tiêu đề!' }]}
            >
              <Input 
                value={formData.initTitle}
                onChange={(e) => setFormData(prev => ({ ...prev, initTitle: e.target.value }))}
                placeholder="VD: Bạn cùng phòng, Đồng nghiệp..."
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item 
          label="Ý định/Mục đích" 
          name="intent"
          rules={[{ required: true, message: 'Vui lòng nhập ý định!' }]}
        >
          <Input 
            value={formData.intent}
            onChange={(e) => setFormData(prev => ({ ...prev, intent: e.target.value }))}
            placeholder="VD: Muốn xây dựng tình bạn, Tìm hiểu thêm về người này..."
          />
        </Form.Item>

        <Form.Item 
          label="Chi tiết (tùy chọn)" 
          name="detail"
        >
          <TextArea
            rows={3}
            value={formData.detail}
            onChange={(e) => setFormData(prev => ({ ...prev, detail: e.target.value }))}
            placeholder="Mô tả chi tiết về mối quan hệ này..."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CreateImageModal: React.FC<CreateImageModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onGeneratePrompt,
  isLoading,
  generatePromptLoading,
  referenceAvatar
}) => {
  const [selectedEmotion, setSelectedEmotion] = useState<string | undefined>();
  const [description, setDescription] = useState<string>('');
  const [refinedDescription, setRefinedDescription] = useState<string>('');

  const handleCreate = async () => {
    await onSubmit(selectedEmotion, description);
  };

  const handleGeneratePrompt = async () => {
    try {
      const response = await onGeneratePrompt(selectedEmotion, description);
      setRefinedDescription(response.refinedVisualDescription || '');
      setDescription(response.technicalPrompt || response.description);
    } catch (error) {
      console.error('Error generating prompt:', error);
    }
  };

  const resetForm = () => {
    setSelectedEmotion(undefined);
    setDescription('');
    setRefinedDescription('');
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  return (
    <Modal
      title={
        <Space>
          <CameraOutlined />
          Tạo ảnh cho Character State
        </Space>
      }
      open={isOpen}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Hủy
        </Button>,
        <Button
          key="create"
          type="primary"
          onClick={handleCreate}
          loading={isLoading}
          disabled={!description.trim()}
        >
          Tạo ảnh
        </Button>
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Reference Image section */}
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', marginBottom: 12, fontWeight: 'bold' }}>
            Ảnh tham chiếu (Avatar/State trước):
          </label>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Card 
              size="small" 
              bodyStyle={{ padding: '8px', textAlign: 'center' }}
              style={{ background: '#fafafa', border: '1px solid #f0f0f0', width: '120px' }}
            >
              <div style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div style={{ 
                  width: '100px', 
                  aspectRatio: '2/3', 
                  border: '1px solid #d9d9d9', 
                  borderRadius: '4px',
                  overflow: 'hidden',
                  background: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {referenceAvatar ? (
                    <img src={referenceAvatar} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Reference" />
                  ) : (
                    <UserOutlined style={{ fontSize: '32px', color: '#d9d9d9' }} />
                  )}
                </div>
                <Badge 
                  count="Image 1" 
                  style={{ 
                    backgroundColor: '#108ee9', 
                    position: 'absolute', 
                    bottom: '-10px', 
                    left: '50%', 
                    transform: 'translateX(-50%)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} 
                />
              </div>
            </Card>
          </div>
          {!referenceAvatar && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                (Không có ảnh tham chiếu, AI sẽ tạo nhân vật mới dựa trên mô tả)
              </Text>
            </div>
          )}
        </div>

        <Divider style={{ margin: '8px 0' }} />

        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            Cảm xúc (tùy chọn):
          </label>
          <Select
            placeholder="Chọn cảm xúc"
            value={selectedEmotion}
            onChange={setSelectedEmotion}
            allowClear
            style={{ width: '100%' }}
          >
            {EMOTIONS.map((emotion) => (
              <Select.Option key={emotion} value={emotion}>
                {emotion}
              </Select.Option>
            ))}
          </Select>
        </div>
        
        <div>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            Mô tả bổ sung (tùy chọn):
          </label>
          <TextArea
            placeholder="VD: Đang đứng dưới mưa, mặc bộ đồ vest sang trọng..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ width: '100%' }}
          />
          <div style={{ marginTop: 8, textAlign: 'right' }}>
            <Button
              type="default"
              icon={<RobotOutlined />}
              onClick={handleGeneratePrompt}
              loading={generatePromptLoading}
              disabled={isLoading}
            >
              Viết prompt bằng AI
            </Button>
          </div>
        </div>

        {refinedDescription && (
          <div style={{ 
            background: '#f6ffed', 
            padding: '12px', 
            borderRadius: '6px', 
            border: '1px solid #b7eb8f' 
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: 4, color: '#52c41a' }}>
              <Space>
                <FileTextOutlined />
                Ý tưởng hình ảnh (AI đề xuất):
              </Space>
            </div>
            <Paragraph style={{ marginBottom: 0, fontSize: '13px' }}>
              {refinedDescription}
            </Paragraph>
          </div>
        )}

        {refinedDescription && (
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
              Technical Prompt (English):
            </label>
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              style={{ width: '100%', fontSize: '12px', fontFamily: 'monospace' }}
            />
          </div>
        )}
      </Space>
    </Modal>
  );
};

const CharacterStateDetail: React.FC = () => {
  const { lifeTreeId, characterId, stateId } = useParams<{ 
    lifeTreeId: string; 
    characterId: string; 
    stateId: string; 
  }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [character, setCharacter] = useState<CharacterDetailResponse | null>(null);
  const [characterState, setCharacterState] = useState<CharacterStateWithRelationships | null>(null);
  const [lifetreeDetail, setLifetreeDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [imagePreviewModalOpen, setImagePreviewModalOpen] = useState(false);
  const [createRelationshipModalOpen, setCreateRelationshipModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<CreateImageResponse | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [createImageModalOpen, setCreateImageModalOpen] = useState(false);
  const [approvingImageId, setApprovingImageId] = useState<string | null>(null);

  useEffect(() => {
    if (!lifeTreeId || !characterId || !stateId) {
      navigate('/lifetrees');
      return;
    }
    
    loadData();
  }, [lifeTreeId, characterId, stateId, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load both character detail and lifetree detail in parallel
      const [characterData, lifetreeData] = await Promise.all([
        lifetreeApi.getCharacterDetail(lifeTreeId!, characterId!),
        lifetreeApi.getLifetreeDetail(lifeTreeId!)
      ]);
      
      setCharacter(characterData);
      setLifetreeDetail(lifetreeData);
      
      // Find the specific state
      const state = characterData.states.find(s => s.id === stateId);
      if (!state) {
        showToast('error', 'Không tìm thấy state này');
        navigate(`/lifetrees/${lifeTreeId}/characters/${characterId}`);
        return;
      }
      
      setCharacterState(state);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('error', 'Có lỗi xảy ra khi tải thông tin');
      navigate('/lifetrees');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateState = async (data: UpdateCharacterStateRequest) => {
    try {
      setActionLoading(prev => ({ ...prev, updateState: true }));
      await lifetreeApi.updateCharacterState(lifeTreeId!, characterId!, stateId!, data);
      showToast('success', 'Cập nhật state thành công');
      setUpdateModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error updating state:', error);
      showToast('error', 'Có lỗi xảy ra khi cập nhật state');
    } finally {
      setActionLoading(prev => ({ ...prev, updateState: false }));
    }
  };

  const handleActivateState = async () => {
    try {
      setActionLoading(prev => ({ ...prev, activate: true }));
      await lifetreeApi.activateState(lifeTreeId!, characterId!, stateId!);
      showToast('success', 'Kích hoạt state thành công');
      await loadData();
    } catch (error) {
      console.error('Error activating state:', error);
      showToast('error', 'Có lỗi xảy ra khi kích hoạt state');
    } finally {
      setActionLoading(prev => ({ ...prev, activate: false }));
    }
  };

  const handleCreateImage = async (emotion?: string, description?: string) => {
    try {
      setActionLoading(prev => ({ ...prev, createImage: true }));
      
      // Use the new API with predefined prompt
      const imageData = await lifetreeApi.createStateImageWithPredefinedPrompt(
        lifeTreeId!,
        characterId!,
        stateId!,
        {
          description: description || '',
          emotion: emotion
        }
      );

      // Map to StateImage type (ensure 'tags' exists)
      const mappedImage = {
        ...imageData,
        tags: (imageData as any).tags || []
      } as StateImage;

      // Append new image to the beginning of list without reloading everything
      setCharacterState(prev => (
        prev ? { ...prev, images: [mappedImage, ...(prev.images || [])] } : prev
      ));

      showToast('success', 'Tạo ảnh thành công');
      setCreateImageModalOpen(false);
    } catch (error) {
      console.error('Error creating image:', error);
      showToast('error', 'Có lỗi xảy ra khi tạo ảnh');
    } finally {
      setActionLoading(prev => ({ ...prev, createImage: false }));
    }
  };

  const handleGeneratePrompt = async (emotion?: string, description?: string): Promise<CreateStateImagePromptResponse> => {
    try {
      setActionLoading(prev => ({ ...prev, generatePrompt: true }));
      
      const promptData = await lifetreeApi.createStateImagePrompt(
        lifeTreeId!,
        characterId!,
        stateId!,
        {
          emotion: emotion,
          description: description
        }
      );

      return promptData;
    } catch (error) {
      console.error('Error generating prompt:', error);
      showToast('error', 'Có lỗi xảy ra khi tạo prompt');
      throw error;
    } finally {
      setActionLoading(prev => ({ ...prev, generatePrompt: false }));
    }
  };

  const getReferenceAvatar = (): string | null => {
    if (!lifetreeDetail || !characterState || !character) return null;

    // 1. Find the node containing this state
    const currentNode = lifetreeDetail.nodes.find((n: any) => 
      n.characterStateIds && n.characterStateIds.includes(stateId)
    );

    if (currentNode) {
      // 2. Find parent node
      const parentNode = lifetreeDetail.nodes.find((n: any) => 
        n.nextNodeIds && n.nextNodeIds.includes(currentNode.id)
      );

      if (parentNode) {
        // 3. Find character state in parent node for the same character
        const parentStateId = parentNode.characterStateIds.find((id: string) => 
          character.states.some(s => s.id === id)
        );

        if (parentStateId) {
          const parentState = character.states.find(s => s.id === parentStateId);
          if (parentState?.imageUrl) {
            return parentState.imageUrl;
          }
        }
      }
    }

    // 4. Fallback to character avatar
    return character.imageUrl || null;
  };

  const handleOpenCreateImageModal = () => {
    setCreateImageModalOpen(true);
  };

  const handleApproveImage = async (imageId: string) => {
    try {
      setApprovingImageId(imageId);
      await lifetreeApi.approveStateImage(lifeTreeId!, characterId!, stateId!, { imageId });
      showToast('success', 'Duyệt ảnh thành công');
      setImagePreviewModalOpen(false);
      
      setCharacterState(prev => {
        if (!prev || !prev.images) return prev;
        const newImages = prev.images.map(img => 
          img.id === imageId ? { ...img, status: 'active' } : img
        );
        return { ...prev, images: newImages };
      });
    } catch (error) {
      console.error('Error approving image:', error);
      showToast('error', 'Có lỗi xảy ra khi duyệt ảnh');
    } finally {
      setApprovingImageId(null);
    }
  };

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const handleCreateRelationship = async (data: CreateRelationshipRequest) => {
    try {
      setActionLoading(prev => ({ ...prev, createRelationship: true }));
      const newRelationship = await lifetreeApi.createRelationship(characterId!, stateId!, data);
      showToast('success', 'Tạo mối quan hệ thành công');
      setCreateRelationshipModalOpen(false);
      
      setCharacterState(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          relationships: [newRelationship, ...(prev.relationships || [])]
        }
      });
    } catch (error) {
      console.error('Error creating relationship:', error);
      showToast('error', 'Có lỗi xảy ra khi tạo mối quan hệ');
    } finally {
      setActionLoading(prev => ({ ...prev, createRelationship: false }));
    }
  };

  // Get available characters for relationship creation (exclude current character)
  const getAvailableCharacters = (): CharacterItem[] => {
    if (!lifetreeDetail?.characters) return [];
    
    return lifetreeDetail.characters.filter((char: CharacterItem) => char.id !== characterId);
  };

  // Get nodes using this character state
  const getNodesUsingThisState = () => {
    if (!lifetreeDetail?.nodes || !stateId) return [];
    
    return lifetreeDetail.nodes.filter((node: any) => 
      node.characterStateIds && node.characterStateIds.includes(stateId)
    );
  };

  // Handle set image as character image
  const handleSetAsCharacterImage = async (imageUrl: string) => {
    try {
      setActionLoading(prev => ({ ...prev, setCharacterImage: true }));
      
      // Update character imageUrl using the dedicated API
      await lifetreeApi.updateCharacterImageUrl(lifeTreeId!, characterId!, { imageUrl });
      
      showToast('success', 'Đã đặt làm ảnh nhân vật thành công');
      await loadData();
    } catch (error) {
      console.error('Error setting character image:', error);
      showToast('error', 'Có lỗi xảy ra khi đặt ảnh nhân vật');
    } finally {
      setActionLoading(prev => ({ ...prev, setCharacterImage: false }));
    }
  };

  // Handle set image as node image
  const handleSetAsNodeImage = async (imageUrl: string) => {
    try {
      setActionLoading(prev => ({ ...prev, setNodeImage: true }));
      
      const nodesUsingState = getNodesUsingThisState();
      
      // Update all nodes using this state
      await Promise.all(
        nodesUsingState.map((node: any) => 
          lifetreeApi.updateNodeImageUrl(lifeTreeId!, node.id, { imageUrl })
        )
      );
      
      showToast('success', `Đã đặt làm ảnh cho ${nodesUsingState.length} node(s) thành công`);
      await loadData();
    } catch (error) {
      console.error('Error setting node image:', error);
      showToast('error', 'Có lỗi xảy ra khi đặt ảnh node');
    } finally {
      setActionLoading(prev => ({ ...prev, setNodeImage: false }));
    }
  };

  // Handle set image as lifetree image
  const handleSetAsLifetreeImage = async (imageUrl: string) => {
    try {
      setActionLoading(prev => ({ ...prev, setLifetreeImage: true }));
      
      await lifetreeApi.updateLifetreeImageUrl(lifeTreeId!, { imageUrl });
      
      showToast('success', 'Đã đặt làm ảnh lifetree thành công');
      await loadData();
    } catch (error) {
      console.error('Error setting lifetree image:', error);
      showToast('error', 'Có lỗi xảy ra khi đặt ảnh lifetree');
    } finally {
      setActionLoading(prev => ({ ...prev, setLifetreeImage: false }));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!character || !characterState) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Empty description="Không tìm thấy thông tin state" />
        <Button 
          type="primary"
          onClick={() => navigate('/lifetrees')}
          style={{ marginTop: 16 }}
        >
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  const activeTab = searchParams.get('tab') || '1';

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate(`/lifetrees/${lifeTreeId}/characters/${characterId}`)}
            >
              Quay lại Character Detail
            </Button>
          </Col>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              Chi tiết State
            </Title>
          </Col>
        </Row>
        <Breadcrumb style={{ marginTop: 16 }}>
          <Breadcrumb.Item>
            <Button type="link" onClick={() => navigate('/lifetrees')} style={{ padding: 0 }}>
              Lifetrees
            </Button>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Button type="link" onClick={() => navigate(`/lifetrees/${lifeTreeId}`)} style={{ padding: 0 }}>
              Lifetree Detail
            </Button>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Button type="link" onClick={() => navigate(`/lifetrees/${lifeTreeId}/characters/${characterId}`)} style={{ padding: 0 }}>
              {character.name}
            </Button>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{characterState.name}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      {/* State Header Card */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} md={6}>
            <div style={{ textAlign: 'center' }}>
              {characterState.imageUrl ? (
                <Avatar 
                  size={120}
                  src={characterState.imageUrl}
                  alt={characterState.name}
                  style={{ objectFit: 'cover', objectPosition: 'top center' }}
                />
              ) : (
                <Avatar 
                  size={120}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#f0f0f0', color: '#999' }}
                />
              )}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Space direction="vertical" size="small">
              <Title level={3} style={{ margin: 0 }}>{characterState.name}</Title>
              <Space wrap>
                <Text strong>Tuổi:</Text>
                <Text>{characterState.age}</Text>
              </Space>
              {characterState.alias && (
                <Space wrap>
                  <Text strong>Biệt danh:</Text>
                  <Text>{characterState.alias}</Text>
                </Space>
              )}
              {characterState.occupation && (
                <Space wrap>
                  <Text strong>Nghề nghiệp:</Text>
                  <Text>{characterState.occupation}</Text>
                </Space>
              )}
              {characterState.displayedDescription && (
                <Space wrap>
                  <Text strong>Mô tả ngắn:</Text>
                  <Text>{characterState.displayedDescription}</Text>
                </Space>
              )}
              <div>
                <Tag color={
                  characterState.status === 'active' ? 'green' :
                  characterState.status === 'draft' ? 'orange' : 'red'
                }>
                  {characterState.status.toUpperCase()}
                </Tag>
              </div>
            </Space>
          </Col>
          <Col xs={24} md={6}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              {characterState.status === 'draft' && (
                <Button 
                  type="primary" 
                  icon={<CheckCircleOutlined />}
                  onClick={handleActivateState}
                  loading={actionLoading.activate}
                  style={{ width: '100%' }}
                >
                  Kích hoạt State
                </Button>
              )}
              <Button 
                icon={<EditOutlined />}
                onClick={() => setUpdateModalOpen(true)}
                style={{ width: '100%' }}
              >
                Cập nhật thông tin
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Main Content Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={handleTabChange} size="large">
          <TabPane 
            tab={
              <span>
                <UserOutlined />
                Thông tin cơ bản
              </span>
            } 
            key="1"
          >
            {characterState.description && (
              <Card size="small" style={{ marginBottom: 24 }}>
                <Title level={5}>Mô tả chi tiết</Title>
                <Paragraph>{characterState.description}</Paragraph>
              </Card>
            )}
            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <Card title={<><UserOutlined style={{ marginRight: 8 }} />Ngoại hình</>} size="small">
                  {characterState.appearance.length > 0 ? (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {characterState.appearance.map((item, index) => (
                        <div key={index}>
                          <Text strong>{item.key}:</Text>
                          <br />
                          <Text type="secondary">{item.value}</Text>
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có thông tin ngoại hình" />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card title={<><HeartOutlined style={{ marginRight: 8 }} />Tâm lý bên trong</>} size="small">
                  {characterState.innerSelf.length > 0 ? (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {characterState.innerSelf.map((item, index) => (
                        <div key={index}>
                          <Text strong>{item.key}:</Text>
                          <br />
                          <Text type="secondary">{item.value}</Text>
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có thông tin tâm lý" />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card title={<><TeamOutlined style={{ marginRight: 8 }} />Phong cách quan hệ</>} size="small">
                  {characterState.relationshipStyles.length > 0 ? (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      {characterState.relationshipStyles.map((item, index) => (
                        <div key={index}>
                          <Text strong>{item.key}:</Text>
                          <br />
                          <Text type="secondary">{item.value}</Text>
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có thông tin phong cách quan hệ" />
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <TeamOutlined />
                Mối quan hệ ({characterState.relationships.length})
              </span>
            } 
            key="2"
          >
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setCreateRelationshipModalOpen(true)}
                disabled={getAvailableCharacters().length === 0}
              >
                Thêm mối quan hệ
              </Button>
            </div>
            
            {characterState.relationships.length > 0 ? (
              <Row gutter={[16, 16]}>
                {characterState.relationships.map((rel) => (
                  <Col xs={24} sm={12} md={8} key={rel.id}>
                    <Card size="small" hoverable>
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Text strong>{rel.currentTitle}</Text>
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Đối tượng">{rel.targetId}</Descriptions.Item>
                          <Descriptions.Item label="Điểm số">
                            <Tag color={rel.currentScore >= 0 ? 'green' : 'red'}>
                              {rel.currentScore}
                            </Tag>
                          </Descriptions.Item>
                          {rel.intent && (
                            <Descriptions.Item label="Ý định">{rel.intent}</Descriptions.Item>
                          )}
                        </Descriptions>
                        {rel.detail && (
                          <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {rel.detail}
                            </Text>
                          </div>
                        )}
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty 
                description="Chưa có mối quan hệ nào"
                style={{ padding: '40px 0' }}
              >
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setCreateRelationshipModalOpen(true)}
                  disabled={getAvailableCharacters().length === 0}
                >
                  Tạo mối quan hệ đầu tiên
                </Button>
              </Empty>
            )}
            
            {getAvailableCharacters().length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#fafafa', borderRadius: '6px', marginTop: 16 }}>
                <Text type="secondary">
                  Cần có ít nhất 2 nhân vật trong lifetree để tạo mối quan hệ
                </Text>
              </div>
            )}
          </TabPane>

          <TabPane 
            tab={
              <span>
                <PictureOutlined />
                Ảnh ({characterState.images?.length || 0})
              </span>
            } 
            key="3"
          >
            <ImageGallery 
              images={characterState.images || []}
              onCreateImage={handleOpenCreateImageModal}
              createImageLoading={actionLoading.createImage || false}
              onApproveImage={handleApproveImage}
              onSetAsCharacterImage={handleSetAsCharacterImage}
              onSetAsNodeImage={handleSetAsNodeImage}
              onSetAsLifetreeImage={handleSetAsLifetreeImage}
              approveImageLoading={approvingImageId}
              setCharacterImageLoading={actionLoading.setCharacterImage || false}
              setNodeImageLoading={actionLoading.setNodeImage || false}
              setLifetreeImageLoading={actionLoading.setLifetreeImage || false}
              nodesUsingThisState={getNodesUsingThisState()}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Modals */}
      <UpdateStateModal
        isOpen={updateModalOpen}
        onClose={() => setUpdateModalOpen(false)}
        state={characterState}
        onSubmit={handleUpdateState}
        isLoading={actionLoading.updateState || false}
      />

      <ImagePreviewModal
        isOpen={imagePreviewModalOpen}
        onClose={() => setImagePreviewModalOpen(false)}
        image={selectedImage}
        onApprove={handleApproveImage}
        isLoading={!!approvingImageId}
      />

      <CreateRelationshipModal
        isOpen={createRelationshipModalOpen}
        onClose={() => setCreateRelationshipModalOpen(false)}
        availableCharacters={getAvailableCharacters()}
        onSubmit={handleCreateRelationship}
        isLoading={actionLoading.createRelationship || false}
      />

      <CreateImageModal
        isOpen={createImageModalOpen}
        onClose={() => setCreateImageModalOpen(false)}
        onSubmit={handleCreateImage}
        onGeneratePrompt={handleGeneratePrompt}
        isLoading={actionLoading.createImage || false}
        generatePromptLoading={actionLoading.generatePrompt || false}
        referenceAvatar={getReferenceAvatar()}
      />
    </div>
  );
};

export default CharacterStateDetail; 